import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Package, CalendarDays, MessageSquare, CreditCard,
  DollarSign, Eye, Check, X, Loader2, Lock, LogOut,
  Trash2, Plus, Pencil, ChevronDown, ChevronUp, Image, Upload, ImagePlus,
  Bell, Send, Mail, Users, Sparkles, TrendingUp, Search
} from "lucide-react";

const statusColors = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  confirmed: "text-blue-400 bg-blue-400/10 border-blue-400/30",
  completed: "text-green-400 bg-green-400/10 border-green-400/30",
  cancelled: "text-red-400 bg-red-400/10 border-red-400/30",
  new: "text-blue-400 bg-blue-400/10 border-blue-400/30",
  read: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  responded: "text-green-400 bg-green-400/10 border-green-400/30",
  paid: "text-green-400 bg-green-400/10 border-green-400/30",
  failed: "text-red-400 bg-red-400/10 border-red-400/30",
  expired: "text-gray-400 bg-gray-400/10 border-gray-400/30",
};

function StatusBadge({ status }) {
  const colorClass = statusColors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/30";
  return (
    <span className={`inline-block px-3 py-1 text-xs font-sans tracking-wider uppercase border ${colorClass}`}>
      {status}
    </span>
  );
}

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      onLogin(password);
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0c0c] flex items-center justify-center pt-[72px]">
      <div className="bg-[#161616] border border-[#b8966a]/20 p-12 w-full max-w-md" data-testid="admin-login">
        <div className="text-center mb-8">
          <Lock className="text-[#b8966a] mx-auto mb-4" size={28} />
          <h1 className="font-display text-2xl text-[#f8f5f0] mb-2">Admin Access</h1>
          <p className="font-body text-sm text-[#a19d98]">Enter your admin password to continue</p>
        </div>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body mb-6 focus:border-[#b8966a] transition-colors"
            data-testid="admin-password-input"
          />
          <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="admin-login-btn">
            {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DashboardStats({ stats }) {
  const cards = [
    { label: "Total Products", value: stats.products, icon: <Package size={20} /> },
    { label: "Bookings", value: stats.bookings, icon: <CalendarDays size={20} />, sub: `${stats.pending_bookings} pending` },
    { label: "Inquiries", value: stats.inquiries, icon: <MessageSquare size={20} />, sub: `${stats.new_inquiries} new` },
    { label: "Orders", value: stats.orders, icon: <CreditCard size={20} />, sub: `${stats.paid_orders} paid` },
    { label: "Revenue", value: `$${stats.total_revenue?.toFixed(2) || "0.00"}`, icon: <DollarSign size={20} /> },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
      {cards.map((card) => (
        <div key={card.label} className="bg-[#161616] border border-[#b8966a]/10 p-5">
          <div className="flex items-center gap-2 text-[#b8966a] mb-3">{card.icon}</div>
          <p className="font-sans text-2xl text-[#f8f5f0] mb-1">{card.value}</p>
          <p className="font-sans text-xs text-[#a19d98] tracking-wide uppercase">{card.label}</p>
          {card.sub && <p className="font-body text-xs text-[#b8966a] mt-1">{card.sub}</p>}
        </div>
      ))}
    </div>
  );
}

function BookingsTab({ adminPw }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchBookings = async () => {
    try { const res = await axios.get(`${API}/admin/bookings`, { headers: { "x-admin-password": adminPw } }); setBookings(res.data); }
    catch { toast.error("Failed to load bookings"); } finally { setLoading(false); }
  };
  useEffect(() => { fetchBookings(); }, []);
  const updateStatus = async (id, status) => {
    try { await axios.put(`${API}/admin/bookings/${id}/status`, { status }, { headers: { "x-admin-password": adminPw } }); toast.success(`Booking ${status}`); fetchBookings(); }
    catch { toast.error("Update failed"); }
  };
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;
  return (
    <div className="space-y-4" data-testid="admin-bookings-tab">
      {bookings.length === 0 ? <p className="font-body text-[#a19d98] text-center py-12">No bookings yet</p> : bookings.map((b) => (
        <div key={b.id} className="bg-[#161616] border border-[#b8966a]/10 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div><h3 className="font-heading text-lg text-[#f8f5f0]">{b.customer_name}</h3><p className="font-body text-sm text-[#a19d98]">{b.customer_email} | {b.customer_phone}</p></div>
            <StatusBadge status={b.status} />
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div><p className="overline text-[10px] mb-1">Type</p><p className="font-body text-sm text-[#f8f5f0]">{b.booking_type}</p></div>
            <div><p className="overline text-[10px] mb-1">Date</p><p className="font-body text-sm text-[#f8f5f0]">{b.preferred_date}</p></div>
            <div><p className="overline text-[10px] mb-1">Time</p><p className="font-body text-sm text-[#f8f5f0]">{b.preferred_time}</p></div>
          </div>
          {b.notes && <p className="font-body text-sm text-[#a19d98] mb-4 italic">"{b.notes}"</p>}
          <div className="flex gap-2">
            {b.status === "pending" && (<>
              <button onClick={() => updateStatus(b.id, "confirmed")} className="px-4 py-2 bg-green-500/10 text-green-400 border border-green-400/30 font-sans text-xs tracking-wider uppercase hover:bg-green-500/20 transition-colors flex items-center gap-1" data-testid={`confirm-booking-${b.id}`}><Check size={14} /> Confirm</button>
              <button onClick={() => updateStatus(b.id, "cancelled")} className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-400/30 font-sans text-xs tracking-wider uppercase hover:bg-red-500/20 transition-colors flex items-center gap-1"><X size={14} /> Cancel</button>
            </>)}
            {b.status === "confirmed" && <button onClick={() => updateStatus(b.id, "completed")} className="px-4 py-2 bg-blue-500/10 text-blue-400 border border-blue-400/30 font-sans text-xs tracking-wider uppercase hover:bg-blue-500/20 transition-colors flex items-center gap-1"><Check size={14} /> Mark Complete</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function InquiriesTab({ adminPw }) {
  const [inquiries, setInquiries] = useState([]); const [loading, setLoading] = useState(true);
  const fetchInquiries = async () => { try { const res = await axios.get(`${API}/admin/inquiries`, { headers: { "x-admin-password": adminPw } }); setInquiries(res.data); } catch { toast.error("Failed to load inquiries"); } finally { setLoading(false); } };
  useEffect(() => { fetchInquiries(); }, []);
  const updateStatus = async (id, status) => { try { await axios.put(`${API}/admin/inquiries/${id}/status`, { status }, { headers: { "x-admin-password": adminPw } }); toast.success(`Inquiry marked as ${status}`); fetchInquiries(); } catch { toast.error("Update failed"); } };
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;
  return (
    <div className="space-y-4" data-testid="admin-inquiries-tab">
      {inquiries.length === 0 ? <p className="font-body text-[#a19d98] text-center py-12">No inquiries yet</p> : inquiries.map((inq) => (
        <div key={inq.id} className="bg-[#161616] border border-[#b8966a]/10 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div><h3 className="font-heading text-lg text-[#f8f5f0]">{inq.name}</h3><p className="font-body text-sm text-[#a19d98]">{inq.email}{inq.phone && ` | ${inq.phone}`}</p></div>
            <StatusBadge status={inq.status} />
          </div>
          <p className="overline text-[10px] mb-1">Subject: {inq.subject}</p>
          <p className="font-body text-[#f8f5f0] mb-4 leading-relaxed">{inq.message}</p>
          <div className="flex gap-2">
            {inq.status === "new" && <button onClick={() => updateStatus(inq.id, "read")} className="px-4 py-2 bg-yellow-500/10 text-yellow-400 border border-yellow-400/30 font-sans text-xs tracking-wider uppercase hover:bg-yellow-500/20 transition-colors flex items-center gap-1"><Eye size={14} /> Mark Read</button>}
            {(inq.status === "new" || inq.status === "read") && <button onClick={() => updateStatus(inq.id, "responded")} className="px-4 py-2 bg-green-500/10 text-green-400 border border-green-400/30 font-sans text-xs tracking-wider uppercase hover:bg-green-500/20 transition-colors flex items-center gap-1"><Check size={14} /> Mark Responded</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function OrdersTab({ adminPw }) {
  const [orders, setOrders] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const res = await axios.get(`${API}/admin/orders`, { headers: { "x-admin-password": adminPw } }); setOrders(res.data); } catch { toast.error("Failed to load orders"); } finally { setLoading(false); } })(); }, []);
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;
  return (
    <div className="space-y-4" data-testid="admin-orders-tab">
      {orders.length === 0 ? <p className="font-body text-[#a19d98] text-center py-12">No orders yet</p> : orders.map((order) => (
        <div key={order.id} className="bg-[#161616] border border-[#b8966a]/10 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div><h3 className="font-sans text-sm text-[#f8f5f0]">{order.session_id}</h3>{order.customer_email && <p className="font-body text-sm text-[#a19d98]">{order.customer_email}</p>}</div>
            <StatusBadge status={order.payment_status} />
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <div><p className="overline text-[10px] mb-1">Amount</p><p className="font-sans text-lg text-[#b8966a]">${order.amount?.toFixed(2)}</p></div>
            <div><p className="overline text-[10px] mb-1">Method</p><p className="font-body text-sm text-[#f8f5f0] capitalize">{order.payment_method}</p></div>
            <div><p className="overline text-[10px] mb-1">Product</p><p className="font-body text-sm text-[#f8f5f0]">{order.metadata?.product_name || "N/A"}</p></div>
            <div><p className="overline text-[10px] mb-1">Date</p><p className="font-body text-sm text-[#a19d98]">{order.created_at ? new Date(order.created_at).toLocaleDateString() : "N/A"}</p></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MembershipsTab({ adminPw }) {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/memberships`, { headers: { "x-admin-password": adminPw } });
        setMemberships(res.data);
      } catch { toast.error("Failed to load memberships"); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;

  const active = memberships.filter((m) => m.status === "active");
  const mrr = active.reduce((s, m) => s + (m.frequency === "monthly" ? (m.amount || 0) : 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-memberships-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-2">
        <div className="bg-[#161616] border border-[#b8966a]/10 p-5"><p className="overline text-[10px] mb-1">Total</p><p className="font-display text-2xl text-[#f8f5f0]">{memberships.length}</p></div>
        <div className="bg-[#161616] border border-[#b8966a]/10 p-5"><p className="overline text-[10px] mb-1">Active</p><p className="font-display text-2xl text-[#b8966a]">{active.length}</p></div>
        <div className="bg-[#161616] border border-[#b8966a]/10 p-5"><p className="overline text-[10px] mb-1">Monthly MRR</p><p className="font-display text-2xl text-[#b8966a]">${mrr.toFixed(0)}</p></div>
      </div>
      {memberships.length === 0 ? <p className="font-body text-[#a19d98] text-center py-12">No memberships yet</p> : memberships.map((m) => (
        <div key={m.id} className="bg-[#161616] border border-[#b8966a]/10 p-6" data-testid={`admin-membership-${m.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div><h3 className="font-heading text-lg text-[#f8f5f0]">{m.tier_name}</h3><p className="font-body text-sm text-[#a19d98]">{m.name} · {m.email}</p></div>
            <StatusBadge status={m.status} />
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <div><p className="overline text-[10px] mb-1">Billing</p><p className="font-sans text-sm text-[#b8966a] capitalize">{m.frequency} · ${m.amount?.toFixed(2)}</p></div>
            <div><p className="overline text-[10px] mb-1">Method</p><p className="font-body text-sm text-[#f8f5f0] capitalize">{m.payment_method}</p></div>
            <div><p className="overline text-[10px] mb-1">Floral Style</p><p className="font-body text-sm text-[#f8f5f0]">{m.arrangement || "Designer's Choice"}</p></div>
            <div><p className="overline text-[10px] mb-1">Started</p><p className="font-body text-sm text-[#a19d98]">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "N/A"}</p></div>
          </div>
          {(m.phone || m.company || m.address) && (
            <div className="mt-4 pt-4 border-t border-[#b8966a]/10 grid md:grid-cols-3 gap-4">
              {m.phone && <div><p className="overline text-[10px] mb-1">Phone</p><p className="font-body text-sm text-[#a19d98]">{m.phone}</p></div>}
              {m.company && <div><p className="overline text-[10px] mb-1">Company</p><p className="font-body text-sm text-[#a19d98]">{m.company}</p></div>}
              {m.address && <div><p className="overline text-[10px] mb-1">Address</p><p className="font-body text-sm text-[#a19d98]">{m.address}</p></div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


// ============ PRODUCT FORM (Create / Edit) ============

const emptyForm = { name: "", description: "", price: "", category: "bouquet", image_url: "", featured: false, in_stock: true, has_variants: false, variants: [], tagline: "", drop_month: "" };
const categories = ["bouquet", "arrangement", "bespoke", "event", "subscription"];

function ProductForm({ initial, onSave, onCancel, adminPw }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? {
    ...emptyForm,
    ...initial,
    price: String(initial.price || ""),
    variants: initial.variants || [],
    tagline: initial.tagline || "",
    drop_month: initial.drop_month || ""
  } : { ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addVariant = () => set("variants", [...form.variants, { quantity: "", price: "", label: "" }]);
  const removeVariant = (i) => set("variants", form.variants.filter((_, idx) => idx !== i));
  const updateVariant = (i, key, val) => {
    const updated = [...form.variants];
    updated[i] = { ...updated[i], [key]: val };
    set("variants", updated);
  };

  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPEG, PNG, WebP, and GIF images are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Max 10MB.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { "x-admin-password": adminPw, "Content-Type": "multipart/form-data" }
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      set("image_url", `${backendUrl}${res.data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  }, [adminPw]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadFile(e.dataTransfer.files[0]);
    }
  }, [uploadFile]);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      uploadFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.description || !form.price || !form.image_url) {
      toast.error("Please fill in all required fields"); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        category: form.category,
        image_url: form.image_url,
        featured: form.featured,
        in_stock: form.in_stock,
        has_variants: form.has_variants,
        variants: form.has_variants ? form.variants.map(v => ({
          quantity: parseInt(v.quantity) || 0,
          price: parseFloat(v.price) || 0,
          label: v.label
        })) : null,
        tagline: form.tagline || null,
        drop_month: form.drop_month || null,
      };

      if (isEdit) {
        await axios.put(`${API}/admin/products/${initial.id}`, payload, { headers: { "x-admin-password": adminPw } });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: { "x-admin-password": adminPw } });
        toast.success("Product created");
      }
      onSave();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || (isEdit ? "Update failed" : "Creation failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#161616] border border-[#b8966a]/20 p-8" data-testid="product-form">
      <div className="flex items-center justify-between mb-8">
        <h3 className="font-heading text-xl text-[#f8f5f0]">{isEdit ? "Edit Product" : "New Product"}</h3>
        <button onClick={onCancel} className="text-[#a19d98] hover:text-[#f8f5f0] transition-colors" data-testid="product-form-cancel"><X size={20} /></button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name & Category */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">Product Name *</label>
            <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Midnight Bloom" required
              className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="product-form-name" />
          </div>
          <div>
            <label className="overline block mb-2">Category *</label>
            <select value={form.category} onChange={e => set("category", e.target.value)}
              className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="product-form-category">
              {categories.map(c => <option key={c} value={c} className="bg-[#0c0c0c]">{c.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="overline block mb-2">Description *</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} placeholder="Describe the product..." required
            className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="product-form-description" />
        </div>

        {/* Price */}
        <div>
          <label className="overline block mb-2">Base Price ($) *</label>
          <input type="number" step="0.01" min="0" value={form.price} onChange={e => set("price", e.target.value)} placeholder="85.00" required
            className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="product-form-price" />
        </div>

        {/* Image Upload */}
        <div>
          <label className="overline block mb-3">Product Image *</label>
          
          {/* Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed cursor-pointer transition-all duration-300 ${
              dragActive
                ? "border-[#b8966a] bg-[#b8966a]/5"
                : form.image_url
                  ? "border-[#b8966a]/30 bg-[#0c0c0c]"
                  : "border-[#b8966a]/20 bg-[#0c0c0c] hover:border-[#b8966a]/40"
            }`}
            data-testid="image-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="image-file-input"
            />

            {uploading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="animate-spin text-[#b8966a] mb-3" size={32} />
                <p className="font-sans text-sm text-[#a19d98]">Uploading...</p>
              </div>
            ) : form.image_url ? (
              <div className="flex items-center gap-6 p-4">
                <img
                  src={form.image_url}
                  alt="Preview"
                  className="w-24 h-24 object-cover border border-[#b8966a]/20 flex-shrink-0"
                  onError={e => { e.target.src = ""; e.target.alt = "Failed to load"; }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-[#f8f5f0] mb-1 truncate">{form.image_url.split('/').pop()}</p>
                  <p className="font-body text-xs text-[#a19d98] mb-3">Click or drag a new image to replace</p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); set("image_url", ""); }}
                    className="font-sans text-[10px] tracking-wider uppercase text-red-400/70 hover:text-red-400 transition-colors flex items-center gap-1"
                    data-testid="remove-image-btn"
                  >
                    <X size={12} /> Remove Image
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 border border-[#b8966a]/20 flex items-center justify-center mb-4">
                  <ImagePlus className="text-[#b8966a]" size={28} />
                </div>
                <p className="font-sans text-sm text-[#f8f5f0] mb-1">
                  Drag & drop an image here
                </p>
                <p className="font-body text-xs text-[#a19d98]">
                  or click to browse — JPEG, PNG, WebP, GIF up to 10MB
                </p>
              </div>
            )}
          </div>

          {/* URL Fallback */}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => {
                const url = window.prompt("Enter image URL:", form.image_url || "");
                if (url !== null) set("image_url", url);
              }}
              className="font-sans text-[10px] tracking-wider uppercase text-[#a19d98] hover:text-[#b8966a] transition-colors flex items-center gap-1"
              data-testid="paste-url-btn"
            >
              <Image size={12} /> Or paste an image URL
            </button>
          </div>
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={e => set("featured", e.target.checked)} className="w-4 h-4" data-testid="product-form-featured" />
            <span className="font-sans text-sm text-[#f8f5f0]">Featured</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.in_stock} onChange={e => set("in_stock", e.target.checked)} className="w-4 h-4" />
            <span className="font-sans text-sm text-[#f8f5f0]">In Stock</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.has_variants} onChange={e => set("has_variants", e.target.checked)} className="w-4 h-4" data-testid="product-form-has-variants" />
            <span className="font-sans text-sm text-[#f8f5f0]">Has Variants (Quantity Options)</span>
          </label>
        </div>

        {/* Capsule Fields */}
        {form.category === "bouquet" && (
          <div className="grid md:grid-cols-2 gap-6 p-4 border border-[#b8966a]/10 bg-[#0c0c0c]">
            <div>
              <label className="overline block mb-2">Tagline</label>
              <input value={form.tagline} onChange={e => set("tagline", e.target.value)} placeholder="for the moments that felt..."
                className="w-full bg-transparent border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" />
            </div>
            <div>
              <label className="overline block mb-2">Drop Month</label>
              <input value={form.drop_month} onChange={e => set("drop_month", e.target.value)} placeholder="May"
                className="w-full bg-transparent border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" />
            </div>
          </div>
        )}

        {/* Variants */}
        {form.has_variants && (
          <div className="space-y-4 p-4 border border-[#b8966a]/10 bg-[#0c0c0c]">
            <div className="flex items-center justify-between">
              <p className="overline">Price Variants</p>
              <button type="button" onClick={addVariant} className="flex items-center gap-1 font-sans text-xs tracking-wider uppercase text-[#b8966a] hover:text-[#cfae82] transition-colors" data-testid="add-variant-btn">
                <Plus size={14} /> Add Variant
              </button>
            </div>
            {form.variants.map((v, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 items-end">
                <div>
                  <label className="font-sans text-[10px] text-[#a19d98] uppercase tracking-wider block mb-1">Label</label>
                  <input value={v.label} onChange={e => updateVariant(i, "label", e.target.value)} placeholder="25 Roses"
                    className="w-full bg-transparent border border-[#b8966a]/20 px-3 py-2 text-[#f8f5f0] text-sm font-body focus:border-[#b8966a] transition-colors" data-testid={`variant-label-${i}`} />
                </div>
                <div>
                  <label className="font-sans text-[10px] text-[#a19d98] uppercase tracking-wider block mb-1">Qty</label>
                  <input type="number" value={v.quantity} onChange={e => updateVariant(i, "quantity", e.target.value)} placeholder="25"
                    className="w-full bg-transparent border border-[#b8966a]/20 px-3 py-2 text-[#f8f5f0] text-sm font-body focus:border-[#b8966a] transition-colors" data-testid={`variant-qty-${i}`} />
                </div>
                <div>
                  <label className="font-sans text-[10px] text-[#a19d98] uppercase tracking-wider block mb-1">Price ($)</label>
                  <input type="number" step="0.01" value={v.price} onChange={e => updateVariant(i, "price", e.target.value)} placeholder="85.00"
                    className="w-full bg-transparent border border-[#b8966a]/20 px-3 py-2 text-[#f8f5f0] text-sm font-body focus:border-[#b8966a] transition-colors" data-testid={`variant-price-${i}`} />
                </div>
                <button type="button" onClick={() => removeVariant(i)} className="px-3 py-2 text-red-400/60 hover:text-red-400 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {form.variants.length === 0 && <p className="font-body text-sm text-[#a19d98] text-center py-4">No variants added. Click "Add Variant" above.</p>}
          </div>
        )}

        {/* Submit */}
        <div className="flex gap-4 pt-4">
          <button type="submit" disabled={saving} className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50" data-testid="product-form-submit">
            {saving ? <Loader2 className="animate-spin" size={16} /> : isEdit ? "Save Changes" : "Create Product"}
          </button>
          <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ============ PRODUCTS TAB ============

function ProductsTab({ adminPw }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false); // 'create' | product object for edit | false
  const fetchProducts = async () => {
    try { const res = await axios.get(`${API}/admin/products`, { headers: { "x-admin-password": adminPw } }); setProducts(res.data); }
    catch { toast.error("Failed to load products"); } finally { setLoading(false); }
  };
  useEffect(() => { fetchProducts(); }, []);

  const toggleStock = async (id, cur) => { try { await axios.put(`${API}/admin/products/${id}`, { in_stock: !cur }, { headers: { "x-admin-password": adminPw } }); toast.success("Updated"); fetchProducts(); } catch { toast.error("Failed"); } };
  const toggleFeatured = async (id, cur) => { try { await axios.put(`${API}/admin/products/${id}`, { featured: !cur }, { headers: { "x-admin-password": adminPw } }); toast.success("Updated"); fetchProducts(); } catch { toast.error("Failed"); } };
  const deleteProduct = async (id) => { if (!window.confirm("Delete this product?")) return; try { await axios.delete(`${API}/admin/products/${id}`, { headers: { "x-admin-password": adminPw } }); toast.success("Deleted"); fetchProducts(); } catch { toast.error("Failed"); } };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;

  // Show form
  if (showForm) {
    return <ProductForm
      initial={showForm === "create" ? null : showForm}
      adminPw={adminPw}
      onCancel={() => setShowForm(false)}
      onSave={() => { setShowForm(false); fetchProducts(); }}
    />;
  }

  return (
    <div data-testid="admin-products-tab">
      {/* Add Product Button */}
      <div className="flex justify-end mb-6">
        <button onClick={() => setShowForm("create")} className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
          <Plus size={16} /> Add Product
        </button>
      </div>

      <div className="space-y-4">
        {products.map((p) => (
          <div key={p.id} className="bg-[#161616] border border-[#b8966a]/10 p-6 flex flex-col md:flex-row gap-6">
            <img src={p.image_url} alt={p.name} className="w-20 h-20 object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="font-heading text-lg text-[#f8f5f0]">{p.name}</h3>
                <span className="font-sans text-xs tracking-wider uppercase text-[#a19d98] border border-[#b8966a]/10 px-2 py-0.5">{p.category}</span>
                {p.featured && <span className="font-sans text-[10px] tracking-wider uppercase text-[#b8966a] bg-[#b8966a]/10 px-2 py-0.5">Featured</span>}
                {!p.in_stock && <span className="font-sans text-[10px] tracking-wider uppercase text-red-400 bg-red-400/10 px-2 py-0.5">Out of Stock</span>}
              </div>
              <p className="font-sans text-lg text-[#b8966a] mb-1">${p.price?.toFixed(2)}</p>
              {p.has_variants && p.variants && <p className="font-body text-xs text-[#a19d98]">Variants: {p.variants.map(v => `${v.label} ($${v.price})`).join(", ")}</p>}
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <button onClick={() => setShowForm(p)} className="px-3 py-1.5 font-sans text-[10px] tracking-wider uppercase border border-[#b8966a]/20 text-[#a19d98] hover:text-[#b8966a] hover:border-[#b8966a]/50 transition-all flex items-center justify-center gap-1" data-testid={`edit-product-${p.id}`}>
                <Pencil size={10} /> Edit
              </button>
              <button onClick={() => toggleFeatured(p.id, p.featured)} className="px-3 py-1.5 font-sans text-[10px] tracking-wider uppercase border border-[#b8966a]/20 text-[#a19d98] hover:text-[#b8966a] hover:border-[#b8966a]/50 transition-all">
                {p.featured ? "Unfeature" : "Feature"}
              </button>
              <button onClick={() => toggleStock(p.id, p.in_stock)} className="px-3 py-1.5 font-sans text-[10px] tracking-wider uppercase border border-[#b8966a]/20 text-[#a19d98] hover:text-[#b8966a] hover:border-[#b8966a]/50 transition-all">
                {p.in_stock ? "Mark OOS" : "In Stock"}
              </button>
              <button onClick={() => deleteProduct(p.id)} className="px-3 py-1.5 font-sans text-[10px] tracking-wider uppercase border border-red-400/20 text-red-400/60 hover:text-red-400 hover:border-red-400/50 transition-all flex items-center justify-center gap-1">
                <Trash2 size={10} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ EVENT TICKETS TAB ============

// ============ SUBSCRIBERS TAB ============

function SubscribersTab({ adminPw }) {
  const [data, setData] = useState({ subscribers: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showNotify, setShowNotify] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ subject: "New Capsule Drop — Fleuris Noir", drop_name: "", drop_price: "", drop_url: "" });
  const [sending, setSending] = useState(false);

  const fetchSubscribers = async () => {
    try { const res = await axios.get(`${API}/admin/subscribers`, { headers: { "x-admin-password": adminPw } }); setData(res.data); }
    catch { toast.error("Failed to load subscribers"); } finally { setLoading(false); }
  };
  useEffect(() => { fetchSubscribers(); }, []);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyForm.drop_name) { toast.error("Please enter a drop name"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/notify-subscribers`, notifyForm, { headers: { "x-admin-password": adminPw } });
      toast.success(res.data.message);
      setShowNotify(false);
      setNotifyForm({ subject: "New Capsule Drop — Fleuris Noir", drop_name: "", drop_price: "", drop_url: "" });
    } catch { toast.error("Failed to send notifications"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;

  return (
    <div data-testid="admin-subscribers-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell size={18} className="text-[#b8966a]" />
          <p className="font-sans text-sm text-[#f8f5f0]"><span className="text-[#b8966a] font-medium">{data.total}</span> subscribers</p>
        </div>
        <button onClick={() => setShowNotify(!showNotify)} className="btn-primary flex items-center gap-2" data-testid="send-notification-btn">
          <Send size={14} /> Send Drop Alert
        </button>
      </div>

      {/* Notify Form */}
      {showNotify && (
        <div className="bg-[#161616] border border-[#b8966a]/20 p-8 mb-6" data-testid="notify-blast-form">
          <h3 className="font-heading text-xl text-[#f8f5f0] mb-6">Send Drop Notification</h3>
          <p className="font-body text-sm text-[#a19d98] mb-6">
            This will email all {data.total} active subscribers about the new drop.
          </p>
          <form onSubmit={handleNotify} className="space-y-4">
            <div>
              <label className="overline block mb-2">Email Subject</label>
              <input value={notifyForm.subject} onChange={e => setNotifyForm({ ...notifyForm, subject: e.target.value })}
                className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="notify-subject" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Drop Name *</label>
                <input value={notifyForm.drop_name} onChange={e => setNotifyForm({ ...notifyForm, drop_name: e.target.value })} placeholder="e.g. The Letter" required
                  className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="notify-drop-name" />
              </div>
              <div>
                <label className="overline block mb-2">Price</label>
                <input value={notifyForm.drop_price} onChange={e => setNotifyForm({ ...notifyForm, drop_price: e.target.value })} placeholder="85.00"
                  className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="notify-drop-price" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Product URL</label>
              <input value={notifyForm.drop_url} onChange={e => setNotifyForm({ ...notifyForm, drop_url: e.target.value })} placeholder="https://www.fleurisnoir.com/collection/..."
                className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="notify-drop-url" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={sending} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="send-blast-btn">
                {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                {sending ? "Sending..." : `Send to ${data.total} Subscribers`}
              </button>
              <button type="button" onClick={() => setShowNotify(false)} className="btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Subscriber List */}
      {data.subscribers.length === 0 ? (
        <div className="bg-[#161616] border border-[#b8966a]/10 p-8 text-center">
          <Bell className="text-[#b8966a]/40 mx-auto mb-3" size={32} />
          <p className="font-body text-[#a19d98]">No subscribers yet</p>
        </div>
      ) : (
        <div className="bg-[#161616] border border-[#b8966a]/10">
          <div className="grid grid-cols-3 gap-4 p-4 border-b border-[#b8966a]/10">
            <p className="overline text-[10px]">Email</p>
            <p className="overline text-[10px]">Name</p>
            <p className="overline text-[10px]">Subscribed</p>
          </div>
          {data.subscribers.map((sub) => (
            <div key={sub.id} className="grid grid-cols-3 gap-4 p-4 border-b border-[#b8966a]/5 last:border-b-0">
              <p className="font-body text-sm text-[#f8f5f0] truncate flex items-center gap-2">
                <Mail size={14} className="text-[#b8966a] flex-shrink-0" />{sub.email}
              </p>
              <p className="font-body text-sm text-[#a19d98]">{sub.name || "—"}</p>
              <p className="font-body text-xs text-[#a19d98]">
                {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FunnelTab({ adminPw }) {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/admin/funnel`, { headers: { "x-admin-password": adminPw } }),
        axios.get(`${API}/admin/nudge-abandoned/preview`, { headers: { "x-admin-password": adminPw } }),
      ]);
      setStats(s.data); setPending(p.data);
    } catch { toast.error("Failed to load funnel"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendNudges = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/nudge-abandoned`, {}, { headers: { "x-admin-password": adminPw } });
      toast.success(res.data.message);
      await load();
    } catch { toast.error("Failed to send reminders"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;

  const cards = [
    { label: "Popup Views", value: stats.popup_views },
    { label: "Dismissed", value: stats.popup_dismissed },
    { label: "Signups", value: stats.signups },
    { label: "Codes Redeemed", value: stats.codes_redeemed },
    { label: "Codes Unused", value: stats.codes_unused },
    { label: "View → Signup", value: `${stats.view_to_signup_rate}%` },
    { label: "Signup → Purchase", value: `${stats.signup_to_purchase_rate}%` },
  ];

  return (
    <div data-testid="admin-funnel-tab">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp size={18} className="text-[#b8966a]" />
        <p className="font-sans text-sm text-[#f8f5f0]">Inner Circle · 15% Welcome Funnel</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#161616] border border-[#b8966a]/15 p-5" data-testid={`funnel-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="font-display text-3xl text-[#f8f5f0] mb-1">{c.value}</p>
            <p className="overline text-[10px]">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#161616] border border-[#b8966a]/20 p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h3 className="font-heading text-xl text-[#f8f5f0] mb-2">Abandoned Code Reminders</h3>
            <p className="font-body text-sm text-[#a19d98] max-w-xl leading-relaxed">
              Email a gentle reminder to subscribers who grabbed a 15% code but haven't ordered.
              A first reminder goes out after 2 days, and a final reminder after 7 days — each sent only once per subscriber.
            </p>
            <p className="font-body text-sm text-[#b8966a] mt-4">
              {pending?.total_pending || 0} reminder(s) ready to send
              <span className="text-[#a19d98]"> · {pending?.stage1_pending || 0} first · {pending?.stage2_pending || 0} final</span>
            </p>
          </div>
          <button onClick={sendNudges} disabled={sending || !(pending?.total_pending)}
            className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="send-nudges-btn">
            {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            {sending ? "Sending..." : "Send Reminders Now"}
          </button>
        </div>
      </div>
    </div>
  );
}


const tabs = [
  { id: "bookings", label: "Bookings", icon: <CalendarDays size={16} /> },
  { id: "inquiries", label: "Inquiries", icon: <MessageSquare size={16} /> },
  { id: "orders", label: "Orders", icon: <CreditCard size={16} /> },
  { id: "products", label: "Products", icon: <Package size={16} /> },
  { id: "memberships", label: "Memberships", icon: <Sparkles size={16} /> },
  { id: "subscribers", label: "Subscribers", icon: <Bell size={16} /> },
  { id: "funnel", label: "Funnel", icon: <TrendingUp size={16} /> },
];

export default function Admin() {
  const [adminPw, setAdminPw] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("bookings");

  useEffect(() => {
    if (!adminPw) return;
    const fetchStats = async () => {
      try { const res = await axios.get(`${API}/admin/dashboard`, { headers: { "x-admin-password": adminPw } }); setStats(res.data); }
      catch { toast.error("Failed to load dashboard"); setAdminPw(null); }
    };
    fetchStats();
  }, [adminPw]);

  if (!adminPw) return <LoginScreen onLogin={setAdminPw} />;

  return (
    <div className="page-transition pt-[72px] min-h-screen bg-[#0c0c0c]" data-testid="admin-dashboard">
      <div className="container-custom py-8">
        <div className="flex items-center justify-between mb-8">
          <div><p className="overline mb-2">Management</p><h1 className="font-display text-3xl text-[#f8f5f0]">Admin Dashboard</h1></div>
          <button onClick={() => setAdminPw(null)} className="flex items-center gap-2 font-sans text-xs tracking-wider uppercase text-[#a19d98] hover:text-[#b8966a] transition-colors" data-testid="admin-logout"><LogOut size={16} /> Sign Out</button>
        </div>
        {stats && <DashboardStats stats={stats} />}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 font-sans text-xs tracking-wider uppercase border transition-all whitespace-nowrap ${activeTab === tab.id ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]" : "bg-transparent border-[#b8966a]/20 text-[#a19d98] hover:border-[#b8966a]/50"}`}
              data-testid={`admin-tab-${tab.id}`}>{tab.icon}{tab.label}</button>
          ))}
        </div>
        {activeTab === "bookings" && <BookingsTab adminPw={adminPw} />}
        {activeTab === "inquiries" && <InquiriesTab adminPw={adminPw} />}
        {activeTab === "orders" && <OrdersTab adminPw={adminPw} />}
        {activeTab === "products" && <ProductsTab adminPw={adminPw} />}
        {activeTab === "memberships" && <MembershipsTab adminPw={adminPw} />}
        {activeTab === "subscribers" && <SubscribersTab adminPw={adminPw} />}
        {activeTab === "funnel" && <FunnelTab adminPw={adminPw} />}
      </div>
    </div>
  );
}
